import React, { useMemo } from 'react';
import { useReferral } from '../../contexts/ReferralContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
    Stethoscope, Syringe, Dumbbell, CalendarDays, 
    CheckCircle, AlertTriangle, Info, Hand, Footprints
} from 'lucide-react';

// Configuration for condition-specific options
const CONDITION_CONFIG = {
    // ==================== PROSTHETIC JOINT PROBLEM (PJP) CONDITIONS ====================
    // These have DIFFERENT requirements - no physio needed, specific surgical history
    
    prosthetic_joint_problem_hip: {
        injectionTypes: [], // Usually not relevant for PJP
        surgicalOptions: [], // Don't show generic options - PJP has specific prosthetic history
        prostheticOptions: {
            enabled: true,
            required: true, // Must document prosthetic history for PJP
            label: 'Prosthetic Hip Details',
            surgeryTypes: [
                { value: 'total_hip_replacement', label: 'Total Hip Replacement (THR)' },
                { value: 'hemiarthroplasty', label: 'Hemiarthroplasty (Half Hip Replacement)' },
                { value: 'dhs', label: 'Dynamic Hip Screw (DHS)' },
                { value: 'cannulated_screws', label: 'Cannulated Screws' },
                { value: 'im_nail', label: 'Intramedullary (Cephalomedullary) Nail' }
            ],
            symptomTypes: [
                { value: 'pain', label: 'Pain' },
                { value: 'instability', label: 'Instability/Dislocation' },
                { value: 'stiffness', label: 'Stiffness' },
                { value: 'leg_length_discrepancy', label: 'Leg Length Discrepancy' },
                { value: 'infection_concern', label: 'Suspected Infection' },
                { value: 'loosening', label: 'Suspected Loosening' },
                { value: 'periprosthetic_fracture', label: 'Periprosthetic Fracture' }
            ],
            revisionOptions: true,
            dislocationHistory: true // Show dislocation count for hip PJP
        },
        otherInterventions: [
            { id: 'walking_aids', label: 'Uses walking aids', hasDetail: true, detailPlaceholder: 'Type (stick, crutches, frame, wheelchair)' }
        ],
        requiresPhysio: false, // PJP does NOT require physio trial
        physioMinSessions: 0,
        isPJP: true
    },
    
    prosthetic_joint_problem_knee: {
        injectionTypes: [], // Usually not relevant for PJP
        surgicalOptions: [], // Don't show generic options
        prostheticOptions: {
            enabled: true,
            required: true,
            label: 'Prosthetic Knee Details',
            surgeryTypes: [
                { value: 'total_knee_replacement', label: 'Total Knee Replacement (TKR)' },
                { value: 'partial_knee_replacement', label: 'Partial (Unicompartmental) Knee Replacement (UKR)' }
            ],
            symptomTypes: [
                { value: 'pain', label: 'Pain' },
                { value: 'instability', label: 'Instability' },
                { value: 'stiffness', label: 'Stiffness' },
                { value: 'infection_concern', label: 'Suspected Infection' },
                { value: 'loosening', label: 'Suspected Loosening' },
                { value: 'periprosthetic_fracture', label: 'Periprosthetic Fracture' },
                { value: 'malalignment', label: 'Malalignment' }
            ],
            revisionOptions: true
        },
        otherInterventions: [
            { id: 'walking_aids', label: 'Uses walking aids', hasDetail: true, detailPlaceholder: 'Type (stick, crutches, frame)' }
        ],
        requiresPhysio: false, // PJP does NOT require physio trial
        physioMinSessions: 0,
        isPJP: true
    },
    
    prosthetic_joint_problem_shoulder: {
        injectionTypes: [],
        surgicalOptions: [],
        prostheticOptions: {
            enabled: true,
            required: true,
            label: 'Prosthetic Shoulder Details',
            surgeryTypes: [
                { value: 'total_shoulder_arthroplasty', label: 'Total Shoulder Arthroplasty (TSA)' },
                { value: 'hemiarthroplasty', label: 'Shoulder Hemiarthroplasty' },
                { value: 'reverse_shoulder_arthroplasty', label: 'Reverse Shoulder Arthroplasty (RSA)' }
            ],
            symptomTypes: [
                { value: 'pain', label: 'Pain' },
                { value: 'instability', label: 'Instability' },
                { value: 'stiffness', label: 'Stiffness' },
                { value: 'weakness', label: 'Weakness' },
                { value: 'infection_concern', label: 'Suspected Infection' },
                { value: 'loosening', label: 'Suspected Loosening' },
                { value: 'rotator_cuff_failure', label: 'Rotator Cuff Failure' }
            ],
            revisionOptions: true
        },
        otherInterventions: [
            { id: 'arm_sling', label: 'Uses arm sling/support' }
        ],
        requiresPhysio: false,
        physioMinSessions: 0,
        isPJP: true
    },

    // ==================== STANDARD CONDITIONS (Non-PJP) ====================
    // Lower limb (hip, knee, ankle, foot)
    hip: {
        injectionTypes: [
            { value: 'intra_articular_steroid', label: 'Intra-articular Corticosteroid' },
            { value: 'intra_articular_ha', label: 'Intra-articular Hyaluronic Acid' },
            { value: 'intra_articular_prp', label: 'Intra-articular PRP' },
            { value: 'trochanteric_bursa', label: 'Trochanteric Bursa Injection' },
            { value: 'iliopsoas_bursa', label: 'Iliopsoas Bursa Injection' }
        ],
        surgicalOptions: [
            { id: 'previous_joint_surgery', label: 'Previous hip surgery (non-arthroplasty)' },
            { id: 'previous_arthroscopy', label: 'Previous hip arthroscopy' },
            { id: 'previous_osteotomy', label: 'Previous osteotomy' }
        ],
        // Prosthetic Joint History - for standard hip conditions (optional)
        prostheticOptions: {
            enabled: true,
            label: 'Previous Hip Fixation',
            surgeryTypes: [
                { value: 'total_hip_replacement', label: 'Total Hip Replacement (THR)' },
                { value: 'hemiarthroplasty', label: 'Hemiarthroplasty (Half Hip Replacement)' },
                { value: 'dhs', label: 'Dynamic Hip Screw (DHS)' },
                { value: 'cannulated_screws', label: 'Cannulated Screws' },
                { value: 'im_nail', label: 'Intramedullary (Cephalomedullary) Nail' }
            ],
            revisionOptions: true
        },
        otherInterventions: [
            { id: 'walking_aids', label: 'Uses walking aids', hasDetail: true, detailPlaceholder: 'Type (stick, crutches, frame, wheelchair)' },
            { id: 'weight_management', label: 'Engaged with weight management' },
            { id: 'hip_brace', label: 'Uses hip brace/support' }
        ],
        requiresPhysio: true,
        physioMinSessions: 6
    },
    knee: {
        injectionTypes: [
            { value: 'intra_articular_steroid', label: 'Intra-articular Corticosteroid' },
            { value: 'intra_articular_ha', label: 'Intra-articular Hyaluronic Acid' },
            { value: 'intra_articular_prp', label: 'Intra-articular PRP' },
            { value: 'genicular_nerve', label: 'Genicular Nerve Block' },
            { value: 'pes_anserine', label: 'Pes Anserine Injection' }
        ],
        surgicalOptions: [
            { id: 'previous_joint_surgery', label: 'Previous knee surgery (non-arthroplasty)' },
            { id: 'previous_arthroscopy', label: 'Previous knee arthroscopy' },
            { id: 'previous_acl_reconstruction', label: 'Previous ACL/ligament reconstruction' },
            { id: 'previous_meniscectomy', label: 'Previous meniscectomy' },
            { id: 'previous_osteotomy', label: 'Previous high tibial osteotomy' }
        ],
        // Prosthetic Joint History - specific to knee
        prostheticOptions: {
            enabled: true,
            label: 'Previous Knee Replacement',
            surgeryTypes: [
                { value: 'total_knee_replacement', label: 'Total Knee Replacement (TKR)' },
                { value: 'partial_knee_replacement', label: 'Partial (Unicompartmental) Knee Replacement' }
            ],
            symptomTypes: [
                { value: 'pain', label: 'Pain' },
                { value: 'instability', label: 'Instability' },
                { value: 'stiffness', label: 'Stiffness' },
                { value: 'infection_concern', label: 'Suspected Infection' }
            ],
            revisionOptions: true
        },
        otherInterventions: [
            { id: 'walking_aids', label: 'Uses walking aids', hasDetail: true, detailPlaceholder: 'Type (stick, crutches, frame)' },
            { id: 'weight_management', label: 'Engaged with weight management' },
            { id: 'knee_brace', label: 'Uses knee brace/support' },
            { id: 'unloader_brace', label: 'Uses unloader brace (valgus/varus)' }
        ],
        requiresPhysio: true,
        physioMinSessions: 6
    },
    ankle: {
        injectionTypes: [
            { value: 'intra_articular_steroid', label: 'Intra-articular Corticosteroid' },
            { value: 'intra_articular_ha', label: 'Intra-articular Hyaluronic Acid' },
            { value: 'subtalar_injection', label: 'Subtalar Joint Injection' },
            { value: 'peroneal_tendon', label: 'Peroneal Tendon Sheath Injection' },
            { value: 'tibialis_posterior', label: 'Tibialis Posterior Tendon Injection' }
        ],
        surgicalOptions: [
            { id: 'previous_joint_surgery', label: 'Previous ankle surgery' },
            { id: 'previous_arthroscopy', label: 'Previous ankle arthroscopy' },
            { id: 'previous_ankle_fusion', label: 'Previous ankle fusion' },
            { id: 'previous_ligament_repair', label: 'Previous ligament repair/reconstruction' }
        ],
        otherInterventions: [
            { id: 'walking_aids', label: 'Uses walking aids', hasDetail: true, detailPlaceholder: 'Type (stick, crutches)' },
            { id: 'ankle_brace', label: 'Uses ankle brace/support' },
            { id: 'custom_orthotics', label: 'Uses custom orthotics/insoles' },
            { id: 'weight_management', label: 'Engaged with weight management' }
        ],
        requiresPhysio: true,
        physioMinSessions: 6
    },
    foot: {
        injectionTypes: [
            { value: 'intra_articular_steroid', label: 'Intra-articular Corticosteroid (MTP joint)' },
            { value: 'plantar_fascia', label: 'Plantar Fascia Injection' },
            { value: 'morton_neuroma', label: "Morton's Neuroma Injection" },
            { value: 'intermetatarsal', label: 'Intermetatarsal Bursa Injection' },
            { value: 'midfoot_joints', label: 'Midfoot Joint Injection' }
        ],
        surgicalOptions: [
            { id: 'previous_bunion_surgery', label: 'Previous bunion surgery (hallux valgus correction)' },
            { id: 'previous_hammertoe', label: 'Previous hammertoe correction' },
            { id: 'previous_metatarsal_surgery', label: 'Previous metatarsal surgery' },
            { id: 'previous_plantar_release', label: 'Previous plantar fascia release' }
        ],
        otherInterventions: [
            { id: 'custom_orthotics', label: 'Uses custom orthotics/insoles' },
            { id: 'toe_spacers', label: 'Uses toe spacers/bunion pads' },
            { id: 'wide_footwear', label: 'Uses wide/accommodative footwear' },
            { id: 'night_splint', label: 'Uses night splint' },
            { id: 'weight_management', label: 'Engaged with weight management' }
        ],
        requiresPhysio: true,
        physioMinSessions: 4
    },
    // Upper limb (shoulder, elbow, hand, wrist)
    shoulder: {
        injectionTypes: [
            { value: 'intra_articular_steroid', label: 'Intra-articular (Glenohumeral) Corticosteroid' },
            { value: 'subacromial', label: 'Subacromial Injection' },
            { value: 'acj_injection', label: 'AC Joint Injection' },
            { value: 'biceps_tendon', label: 'Biceps Tendon Sheath Injection' },
            { value: 'hydrodilatation', label: 'Hydrodilatation (for frozen shoulder)' }
        ],
        surgicalOptions: [
            { id: 'previous_arthroscopy', label: 'Previous shoulder arthroscopy' },
            { id: 'previous_rotator_cuff', label: 'Previous rotator cuff repair' },
            { id: 'previous_stabilization', label: 'Previous shoulder stabilization' },
            { id: 'previous_acj_surgery', label: 'Previous AC joint surgery' }
        ],
        // Prosthetic Joint History - specific to shoulder
        prostheticOptions: {
            enabled: true,
            label: 'Previous Shoulder Replacement',
            surgeryTypes: [
                { value: 'total_shoulder_arthroplasty', label: 'Total Shoulder Arthroplasty (TSA)' },
                { value: 'hemiarthroplasty', label: 'Shoulder Hemiarthroplasty' },
                { value: 'reverse_shoulder_arthroplasty', label: 'Reverse Shoulder Arthroplasty (RSA)' }
            ],
            revisionOptions: true
        },
        otherInterventions: [
            { id: 'shoulder_sling', label: 'Uses shoulder sling/support' },
            { id: 'tens_machine', label: 'Uses TENS machine' },
            { id: 'activity_modification', label: 'Activity modification (work/sport)' }
        ],
        requiresPhysio: true,
        physioMinSessions: 6
    },
    elbow: {
        injectionTypes: [
            { value: 'intra_articular_steroid', label: 'Intra-articular Corticosteroid' },
            { value: 'lateral_epicondyle', label: 'Lateral Epicondyle Injection (Tennis Elbow)' },
            { value: 'medial_epicondyle', label: 'Medial Epicondyle Injection (Golfer\'s Elbow)' },
            { value: 'olecranon_bursa', label: 'Olecranon Bursa Injection' },
            { value: 'cubital_tunnel', label: 'Cubital Tunnel Injection' }
        ],
        surgicalOptions: [
            { id: 'previous_arthroscopy', label: 'Previous elbow arthroscopy' },
            { id: 'previous_elbow_surgery', label: 'Previous elbow surgery' },
            { id: 'previous_epicondyle_release', label: 'Previous epicondyle release' },
            { id: 'previous_ulnar_nerve', label: 'Previous ulnar nerve surgery' }
        ],
        otherInterventions: [
            { id: 'elbow_brace', label: 'Uses elbow brace/support' },
            { id: 'tennis_elbow_strap', label: 'Uses tennis elbow strap' },
            { id: 'activity_modification', label: 'Activity modification (work/sport)' }
        ],
        requiresPhysio: true,
        physioMinSessions: 6
    },
    hand: {
        injectionTypes: [
            { value: 'trigger_finger', label: 'Trigger Finger Injection (A1 pulley)' },
            { value: 'cmc_joint', label: 'CMC (Thumb Base) Joint Injection' },
            { value: 'pip_dip_joint', label: 'PIP/DIP Joint Injection' },
            { value: 'de_quervain', label: "De Quervain's Injection" },
            { value: 'ganglion', label: 'Ganglion Aspiration/Injection' }
        ],
        surgicalOptions: [
            { id: 'previous_trigger_release', label: 'Previous trigger finger release' },
            { id: 'previous_cmc_surgery', label: 'Previous CMC joint surgery (trapeziectomy)' },
            { id: 'previous_ganglion', label: 'Previous ganglion excision' },
            { id: 'previous_tendon_surgery', label: 'Previous tendon surgery' },
            { id: 'previous_dupuytren', label: "Previous Dupuytren's surgery" }
        ],
        otherInterventions: [
            { id: 'thumb_splint', label: 'Uses thumb spica splint' },
            { id: 'finger_splint', label: 'Uses finger splint' },
            { id: 'compression_gloves', label: 'Uses compression gloves' },
            { id: 'activity_modification', label: 'Activity/ergonomic modification' }
        ],
        requiresPhysio: false, // Hand conditions may not always need physio first
        physioMinSessions: 4
    },
    wrist: {
        injectionTypes: [
            { value: 'carpal_tunnel', label: 'Carpal Tunnel Injection' },
            { value: 'radiocarpal_joint', label: 'Radiocarpal Joint Injection' },
            { value: 'de_quervain', label: "De Quervain's (1st Extensor Compartment) Injection" },
            { value: 'tfcc_injection', label: 'TFCC Area Injection' },
            { value: 'ganglion', label: 'Ganglion Aspiration/Injection' }
        ],
        surgicalOptions: [
            { id: 'previous_carpal_tunnel', label: 'Previous carpal tunnel release' },
            { id: 'previous_wrist_surgery', label: 'Previous wrist surgery' },
            { id: 'previous_ganglion', label: 'Previous ganglion excision' },
            { id: 'previous_wrist_fusion', label: 'Previous wrist fusion' },
            { id: 'previous_de_quervain', label: "Previous De Quervain's release" }
        ],
        otherInterventions: [
            { id: 'wrist_splint', label: 'Uses wrist splint/support' },
            { id: 'night_splint', label: 'Uses night splint (for carpal tunnel)' },
            { id: 'ergonomic_changes', label: 'Ergonomic workplace changes' },
            { id: 'activity_modification', label: 'Activity modification' }
        ],
        requiresPhysio: false,
        physioMinSessions: 4
    },
    // Spine
    spine: {
        injectionTypes: [
            { value: 'facet_joint', label: 'Facet Joint Injection' },
            { value: 'caudal_epidural', label: 'Caudal Epidural Injection' },
            { value: 'transforaminal_epidural', label: 'Transforaminal Epidural Injection' },
            { value: 'interlaminar_epidural', label: 'Interlaminar Epidural Injection' },
            { value: 'medial_branch_block', label: 'Medial Branch Block' },
            { value: 'sacroiliac_joint', label: 'Sacroiliac (SI) Joint Injection' },
            { value: 'trigger_point', label: 'Trigger Point Injection' },
            { value: 'nerve_root_block', label: 'Nerve Root Block' }
        ],
        surgicalOptions: [
            { id: 'previous_discectomy', label: 'Previous discectomy/microdiscectomy' },
            { id: 'previous_decompression', label: 'Previous spinal decompression' },
            { id: 'previous_fusion', label: 'Previous spinal fusion' },
            { id: 'previous_disc_replacement', label: 'Previous disc replacement' },
            { id: 'previous_laminectomy', label: 'Previous laminectomy' }
        ],
        otherInterventions: [
            { id: 'back_brace', label: 'Uses back brace/lumbar support' },
            { id: 'tens_machine', label: 'Uses TENS machine' },
            { id: 'ergonomic_changes', label: 'Ergonomic workplace changes' },
            { id: 'weight_management', label: 'Engaged with weight management' },
            { id: 'pain_management', label: 'Under pain management clinic' }
        ],
        requiresPhysio: true,
        physioMinSessions: 6
    }
};

// Default config for unmapped body parts
const DEFAULT_CONFIG = {
    injectionTypes: [
        { value: 'intra_articular_steroid', label: 'Intra-articular Corticosteroid' },
        { value: 'local_anaesthetic', label: 'Local Anaesthetic' },
        { value: 'trigger_point', label: 'Trigger Point Injection' }
    ],
    surgicalOptions: [
        { id: 'previous_joint_surgery', label: 'Previous surgery on this area' }
    ],
    otherInterventions: [
        { id: 'brace_support', label: 'Uses brace/support' },
        { id: 'activity_modification', label: 'Activity modification' }
    ],
    requiresPhysio: true,
    physioMinSessions: 6
};

export default function TreatmentHistoryStep() {
    const { treatment_history, surgical_history, updateTreatmentHistory, updateSurgicalHistory, body_part, condition } = useReferral();

    // Get condition-specific configuration
    // Check for PJP condition first, then fall back to body_part
    const config = useMemo(() => {
        // PJP conditions have their own config
        if (condition && CONDITION_CONFIG[condition]) {
            return CONDITION_CONFIG[condition];
        }
        return CONDITION_CONFIG[body_part] || DEFAULT_CONFIG;
    }, [body_part, condition]);

    const handleTreatmentChange = (field, value) => {
        updateTreatmentHistory({ [field]: value });
    };

    const handleSurgicalChange = (field, value) => {
        updateSurgicalHistory({ [field]: value });
    };

    // Check if conservative treatment is adequate
    const physioAdequate = treatment_history.physiotherapy && 
        treatment_history.physio_sessions >= config.physioMinSessions;
    const analgesiaAdequate = treatment_history.regular_analgesia && 
        treatment_history.analgesia_duration_months >= 3;
    
    // Check if this is a PJP condition
    const isPJP = config.isPJP === true;

    return (
        <div className="space-y-6">
            {/* Guideline Alert - condition specific */}
            {isPJP ? (
                // PJP-specific guidance - NO physio requirement
                <Card className="card-clinical bg-blue-50 border-blue-200">
                    <CardContent className="pt-6">
                        <div className="flex items-start gap-3">
                            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                            <div>
                                <h4 className="font-semibold text-blue-900 mb-1">
                                    Prosthetic Joint Problem Referral
                                </h4>
                                <p className="text-sm text-blue-700">
                                    This is a referral for a prosthetic joint problem. Please ensure:
                                </p>
                                <ul className="text-sm text-blue-700 mt-2 space-y-1">
                                    <li>• Complete details of the original prosthetic surgery</li>
                                    <li>• Document any revision surgeries</li>
                                    <li>• Specify current symptoms with the prosthesis</li>
                                    {body_part === 'hip' && <li>• Document any previous dislocations</li>}
                                </ul>
                                <p className="text-sm text-blue-600 mt-3 italic">
                                    Note: Physiotherapy trial is NOT required for prosthetic joint problem referrals.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            ) : (
                // Standard conservative treatment requirements
                <Card className="card-clinical bg-amber-50 border-amber-200">
                    <CardContent className="pt-6">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                            <div>
                                <h4 className="font-semibold text-amber-900 mb-1">
                                    Conservative Treatment Requirements - {body_part.charAt(0).toUpperCase() + body_part.slice(1)}
                                </h4>
                                <p className="text-sm text-amber-700">
                                    Per NICE/BOA guidelines for {body_part} conditions, patients should have evidence of:
                                </p>
                                <ul className="text-sm text-amber-700 mt-2 space-y-1">
                                    {config.requiresPhysio && (
                                        <li className={`flex items-center gap-2 ${physioAdequate ? 'text-emerald-700' : ''}`}>
                                            {physioAdequate ? <CheckCircle className="w-4 h-4" /> : <span>•</span>}
                                            Physiotherapy (minimum {config.physioMinSessions} sessions)
                                            {physioAdequate && <span className="text-xs bg-emerald-100 px-2 py-0.5 rounded">Met</span>}
                                        </li>
                                    )}
                                    <li className={`flex items-center gap-2 ${analgesiaAdequate ? 'text-emerald-700' : ''}`}>
                                        {analgesiaAdequate ? <CheckCircle className="w-4 h-4" /> : <span>•</span>}
                                        Regular analgesia for at least 3 months
                                        {analgesiaAdequate && <span className="text-xs bg-emerald-100 px-2 py-0.5 rounded">Met</span>}
                                    </li>
                                    {['hip', 'knee', 'ankle', 'foot', 'spine'].includes(body_part) && (
                                        <li>• Weight management consideration (if BMI &gt; 30)</li>
                                    )}
                                </ul>
                                {!physioAdequate && config.requiresPhysio && (
                                    <p className="text-sm text-red-700 mt-3 font-medium">
                                        ⚠️ Referrals without adequate physiotherapy trial may be rejected
                                    </p>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Physiotherapy - only show for non-PJP conditions that require it */}
            {!isPJP && (
            <Card className="card-clinical">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Dumbbell className="w-5 h-5 text-teal-600" />
                        Physiotherapy
                        {config.requiresPhysio && <span className="text-xs text-red-500 ml-2">*Required</span>}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center space-x-3">
                        <Checkbox
                            id="physio"
                            checked={treatment_history.physiotherapy}
                            onCheckedChange={(checked) => handleTreatmentChange('physiotherapy', checked)}
                            data-testid="physio-checkbox"
                        />
                        <Label htmlFor="physio" className="font-medium text-slate-700 cursor-pointer">
                            Patient has had physiotherapy for this {body_part} condition
                        </Label>
                    </div>

                    {!treatment_history.physiotherapy && config.requiresPhysio && (
                        <div className="ml-6 p-3 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-sm text-red-700 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4" />
                                <span><strong>Guidelines require physiotherapy trial</strong> before specialist referral for {body_part} conditions</span>
                            </p>
                            <div className="mt-2 space-y-2">
                                <Label className="text-sm text-red-700">If not trialed, please provide justification:</Label>
                                <Textarea
                                    placeholder="e.g., Patient unable to attend due to mobility issues, Previous physio failed within 6 months, Urgent/red flag symptoms present..."
                                    value={treatment_history.no_physio_justification || ''}
                                    onChange={(e) => handleTreatmentChange('no_physio_justification', e.target.value)}
                                    className="input-clinical border-red-200"
                                    data-testid="no-physio-justification"
                                />
                            </div>
                        </div>
                    )}

                    {treatment_history.physiotherapy && (
                        <div className="ml-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="label-clinical">Number of Sessions</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        placeholder={String(config.physioMinSessions)}
                                        value={treatment_history.physio_sessions || ''}
                                        onChange={(e) => handleTreatmentChange('physio_sessions', parseInt(e.target.value) || 0)}
                                        className="input-clinical"
                                        data-testid="physio-sessions-input"
                                    />
                                    {treatment_history.physio_sessions < config.physioMinSessions && treatment_history.physio_sessions > 0 && (
                                        <p className="text-xs text-amber-600 flex items-center gap-1">
                                            <Info className="w-3 h-3" />
                                            Guidelines recommend minimum {config.physioMinSessions} sessions for {body_part}
                                        </p>
                                    )}
                                    {treatment_history.physio_sessions >= config.physioMinSessions && (
                                        <p className="text-xs text-emerald-600 flex items-center gap-1">
                                            <CheckCircle className="w-3 h-3" />
                                            Meets guideline threshold
                                        </p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label className="label-clinical">Benefit Achieved</Label>
                                    <Select 
                                        value={treatment_history.physio_benefit || ''} 
                                        onValueChange={(value) => handleTreatmentChange('physio_benefit', value)}
                                    >
                                        <SelectTrigger data-testid="physio-benefit-select">
                                            <SelectValue placeholder="Select response" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="significant">Significant improvement</SelectItem>
                                            <SelectItem value="moderate">Moderate improvement</SelectItem>
                                            <SelectItem value="minimal">Minimal improvement</SelectItem>
                                            <SelectItem value="none">No improvement</SelectItem>
                                            <SelectItem value="worse">Symptoms worsened</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
            )}

            {/* Analgesia */}
            <Card className="card-clinical">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Stethoscope className="w-5 h-5 text-teal-600" />
                        Analgesia History
                        <span className="text-xs text-red-500 ml-2">*Required</span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center space-x-3">
                        <Checkbox
                            id="analgesia"
                            checked={treatment_history.regular_analgesia}
                            onCheckedChange={(checked) => handleTreatmentChange('regular_analgesia', checked)}
                            data-testid="analgesia-checkbox"
                        />
                        <Label htmlFor="analgesia" className="font-medium text-slate-700 cursor-pointer">
                            Patient takes regular analgesia for this condition
                        </Label>
                    </div>

                    {!treatment_history.regular_analgesia && (
                        <div className="ml-6 p-3 bg-amber-50 border border-amber-200 rounded-md">
                            <p className="text-sm text-amber-700 flex items-center gap-2">
                                <Info className="w-4 h-4" />
                                Guidelines recommend trial of analgesia before referral
                            </p>
                        </div>
                    )}

                    {treatment_history.regular_analgesia && (
                        <div className="ml-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="label-clinical">Type of Analgesia</Label>
                                    <Select 
                                        value={treatment_history.analgesia_type || ''} 
                                        onValueChange={(value) => handleTreatmentChange('analgesia_type', value)}
                                    >
                                        <SelectTrigger data-testid="analgesia-type-select">
                                            <SelectValue placeholder="Select type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="paracetamol">Paracetamol only</SelectItem>
                                            <SelectItem value="nsaids">NSAIDs only</SelectItem>
                                            <SelectItem value="paracetamol_nsaids">Paracetamol + NSAIDs</SelectItem>
                                            <SelectItem value="weak_opioids">Weak opioids (Codeine/Tramadol)</SelectItem>
                                            <SelectItem value="strong_opioids">Strong opioids</SelectItem>
                                            <SelectItem value="neuropathic">Neuropathic agents (Gabapentin/Pregabalin)</SelectItem>
                                            <SelectItem value="combination">Full combination therapy</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label className="label-clinical">Duration (months)</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        placeholder="3"
                                        value={treatment_history.analgesia_duration_months || ''}
                                        onChange={(e) => handleTreatmentChange('analgesia_duration_months', parseInt(e.target.value) || 0)}
                                        className="input-clinical"
                                        data-testid="analgesia-duration-input"
                                    />
                                    {treatment_history.analgesia_duration_months < 3 && treatment_history.analgesia_duration_months > 0 && (
                                        <p className="text-xs text-amber-600 flex items-center gap-1">
                                            <Info className="w-3 h-3" />
                                            Guidelines recommend minimum 3 months trial
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Injections - Condition Specific Types - Only for non-PJP */}
            {!isPJP && config.injectionTypes && config.injectionTypes.length > 0 && (
            <Card className="card-clinical">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Syringe className="w-5 h-5 text-teal-600" />
                        Injections - {body_part.charAt(0).toUpperCase() + body_part.slice(1)}
                    </CardTitle>
                    <CardDescription>
                        Select injection types relevant to {body_part} conditions
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center space-x-3">
                        <Checkbox
                            id="injections"
                            checked={treatment_history.intra_articular_injections}
                            onCheckedChange={(checked) => handleTreatmentChange('intra_articular_injections', checked)}
                            data-testid="injections-checkbox"
                        />
                        <Label htmlFor="injections" className="font-medium text-slate-700 cursor-pointer">
                            Patient has had injections for this condition
                        </Label>
                    </div>

                    {treatment_history.intra_articular_injections && (
                        <div className="ml-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label className="label-clinical">Injection Type</Label>
                                    <Select 
                                        value={treatment_history.injection_type || ''} 
                                        onValueChange={(value) => handleTreatmentChange('injection_type', value)}
                                    >
                                        <SelectTrigger data-testid="injection-type-select">
                                            <SelectValue placeholder="Select type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {config.injectionTypes.map(type => (
                                                <SelectItem key={type.value} value={type.value}>
                                                    {type.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label className="label-clinical">Number of Injections</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        placeholder="1"
                                        value={treatment_history.injection_count || ''}
                                        onChange={(e) => handleTreatmentChange('injection_count', parseInt(e.target.value) || 0)}
                                        className="input-clinical"
                                        data-testid="injection-count-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="label-clinical">Last Injection Date</Label>
                                    <Input
                                        type="date"
                                        value={treatment_history.last_injection_date || ''}
                                        onChange={(e) => handleTreatmentChange('last_injection_date', e.target.value)}
                                        className="input-clinical"
                                        data-testid="last-injection-date-input"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label className="label-clinical">Response to Injection</Label>
                                <Select 
                                    value={treatment_history.injection_benefit || ''} 
                                    onValueChange={(value) => handleTreatmentChange('injection_benefit', value)}
                                >
                                    <SelectTrigger data-testid="injection-benefit-select">
                                        <SelectValue placeholder="Select response" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="excellent">Excellent - prolonged relief (&gt;3 months)</SelectItem>
                                        <SelectItem value="good">Good - moderate relief (1-3 months)</SelectItem>
                                        <SelectItem value="temporary">Temporary relief only (&lt;1 month)</SelectItem>
                                        <SelectItem value="minimal">Minimal relief</SelectItem>
                                        <SelectItem value="none">No relief</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
            )}

            {/* Previous Surgery - Condition Specific */}
            {/* For PJP: Show ONLY prosthetic history, hide generic surgical options */}
            {/* For non-PJP: Show generic options plus optional prosthetic history */}
            <Card className="card-clinical">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <CalendarDays className="w-5 h-5 text-teal-600" />
                        {isPJP ? 'Prosthetic Joint Details' : `Previous Surgical Interventions - ${body_part.charAt(0).toUpperCase() + body_part.slice(1)}`}
                        {isPJP && <span className="text-xs text-red-500 ml-2">*Required</span>}
                    </CardTitle>
                    {isPJP && (
                        <CardDescription>
                            Document the details of the prosthetic joint surgery
                        </CardDescription>
                    )}
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Standard surgical options - ONLY for non-PJP */}
                    {!isPJP && config.surgicalOptions && config.surgicalOptions.map((option) => (
                        <div key={option.id} className="space-y-2">
                            <div className="flex items-center space-x-3">
                                <Checkbox
                                    id={option.id}
                                    checked={surgical_history[option.id] || false}
                                    onCheckedChange={(checked) => handleSurgicalChange(option.id, checked)}
                                    data-testid={`${option.id}-checkbox`}
                                />
                                <Label htmlFor={option.id} className="font-medium text-slate-700 cursor-pointer">
                                    {option.label}
                                </Label>
                            </div>
                            {surgical_history[option.id] && (
                                <Textarea
                                    placeholder={`Details of ${option.label.toLowerCase()} (type, date, outcome)`}
                                    value={surgical_history[`${option.id}_details`] || ''}
                                    onChange={(e) => handleSurgicalChange(`${option.id}_details`, e.target.value)}
                                    className="ml-6 input-clinical"
                                    data-testid={`${option.id}-details`}
                                />
                            )}
                        </div>
                    ))}

                    {/* Prosthetic Joint History Section - Only for hip, knee, shoulder */}
                    {config.prostheticOptions?.enabled && (
                        <div className={isPJP ? "" : "mt-6 pt-6 border-t border-slate-200"}>
                            <div className="space-y-4">
                                {/* For PJP: Always show prosthetic details (no checkbox needed) */}
                                {/* For non-PJP: Show checkbox to optionally add prosthetic history */}
                                {!isPJP && (
                                    <div className="flex items-center space-x-3">
                                        <Checkbox
                                            id="has_prosthetic_joint"
                                            checked={surgical_history.has_prosthetic_joint || false}
                                            onCheckedChange={(checked) => handleSurgicalChange('has_prosthetic_joint', checked)}
                                            data-testid="has-prosthetic-joint-checkbox"
                                        />
                                        <Label htmlFor="has_prosthetic_joint" className="font-semibold text-slate-800 cursor-pointer">
                                            {config.prostheticOptions.label}
                                        </Label>
                                    </div>
                                )}

                                {/* Show prosthetic details: always for PJP, conditionally for non-PJP */}
                                {(isPJP || surgical_history.has_prosthetic_joint) && (
                                    <div className={isPJP ? "space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200" : "ml-6 space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200"}>
                                        {/* Primary Surgery Section */}
                                        <div className="space-y-3">
                                            <h5 className="font-medium text-slate-700">Primary Surgery Details</h5>
                                            
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label className="label-clinical">Type of Surgery *</Label>
                                                    <Select 
                                                        value={surgical_history.prosthetic_primary_type || ''} 
                                                        onValueChange={(value) => handleSurgicalChange('prosthetic_primary_type', value)}
                                                    >
                                                        <SelectTrigger data-testid="prosthetic-type-select">
                                                            <SelectValue placeholder="Select surgery type" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {config.prostheticOptions.surgeryTypes.map(type => (
                                                                <SelectItem key={type.value} value={type.value}>
                                                                    {type.label}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>

                                                <div className="space-y-2">
                                                    <Label className="label-clinical">Year of Primary Surgery *</Label>
                                                    <Input
                                                        type="number"
                                                        min="1970"
                                                        max={new Date().getFullYear()}
                                                        placeholder="e.g., 2020"
                                                        value={surgical_history.prosthetic_primary_year || ''}
                                                        onChange={(e) => handleSurgicalChange('prosthetic_primary_year', e.target.value)}
                                                        className="input-clinical"
                                                        data-testid="prosthetic-primary-year"
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label className="label-clinical">Surgeon Name</Label>
                                                    <Input
                                                        placeholder="Name of operating surgeon"
                                                        value={surgical_history.prosthetic_primary_surgeon || ''}
                                                        onChange={(e) => handleSurgicalChange('prosthetic_primary_surgeon', e.target.value)}
                                                        className="input-clinical"
                                                        data-testid="prosthetic-primary-surgeon"
                                                    />
                                                </div>

                                                <div className="space-y-2">
                                                    <Label className="label-clinical">Institution</Label>
                                                    <Input
                                                        placeholder="Hospital/Institution name"
                                                        value={surgical_history.prosthetic_primary_institution || ''}
                                                        onChange={(e) => handleSurgicalChange('prosthetic_primary_institution', e.target.value)}
                                                        className="input-clinical"
                                                        data-testid="prosthetic-primary-institution"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Revision Surgery Section */}
                                        {config.prostheticOptions.revisionOptions && (
                                            <div className="space-y-3 pt-4 border-t border-slate-300">
                                                <div className="flex items-center space-x-3">
                                                    <Checkbox
                                                        id="has_revision_surgery"
                                                        checked={surgical_history.has_revision_surgery || false}
                                                        onCheckedChange={(checked) => handleSurgicalChange('has_revision_surgery', checked)}
                                                        data-testid="has-revision-surgery-checkbox"
                                                    />
                                                    <Label htmlFor="has_revision_surgery" className="font-medium text-slate-700 cursor-pointer">
                                                        Previous Revision Surgery
                                                    </Label>
                                                </div>

                                                {surgical_history.has_revision_surgery && (
                                                    <div className="ml-6 space-y-4">
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                            <div className="space-y-2">
                                                                <Label className="label-clinical">Year of Revision</Label>
                                                                <Input
                                                                    type="number"
                                                                    min="1970"
                                                                    max={new Date().getFullYear()}
                                                                    placeholder="e.g., 2023"
                                                                    value={surgical_history.prosthetic_revision_year || ''}
                                                                    onChange={(e) => handleSurgicalChange('prosthetic_revision_year', e.target.value)}
                                                                    className="input-clinical"
                                                                    data-testid="prosthetic-revision-year"
                                                                />
                                                            </div>

                                                            <div className="space-y-2">
                                                                <Label className="label-clinical">Revision Surgeon</Label>
                                                                <Input
                                                                    placeholder="Name of revision surgeon"
                                                                    value={surgical_history.prosthetic_revision_surgeon || ''}
                                                                    onChange={(e) => handleSurgicalChange('prosthetic_revision_surgeon', e.target.value)}
                                                                    className="input-clinical"
                                                                    data-testid="prosthetic-revision-surgeon"
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="space-y-2">
                                                            <Label className="label-clinical">Revision Institution</Label>
                                                            <Input
                                                                placeholder="Hospital/Institution for revision"
                                                                value={surgical_history.prosthetic_revision_institution || ''}
                                                                onChange={(e) => handleSurgicalChange('prosthetic_revision_institution', e.target.value)}
                                                                className="input-clinical"
                                                                data-testid="prosthetic-revision-institution"
                                                            />
                                                        </div>

                                                        <div className="space-y-2">
                                                            <Label className="label-clinical">Reason for Revision</Label>
                                                            <Textarea
                                                                placeholder="e.g., Aseptic loosening, infection, instability, periprosthetic fracture..."
                                                                value={surgical_history.prosthetic_revision_reason || ''}
                                                                onChange={(e) => handleSurgicalChange('prosthetic_revision_reason', e.target.value)}
                                                                className="input-clinical"
                                                                data-testid="prosthetic-revision-reason"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Current Symptoms (for knee) */}
                                        {config.prostheticOptions.symptomTypes && (
                                            <div className="space-y-3 pt-4 border-t border-slate-300">
                                                <Label className="font-medium text-slate-700">Current Symptoms with Prosthesis *</Label>
                                                <div className="grid grid-cols-2 gap-3">
                                                    {config.prostheticOptions.symptomTypes.map((symptom) => (
                                                        <div key={symptom.value} className="flex items-center space-x-3">
                                                            <Checkbox
                                                                id={`symptom_${symptom.value}`}
                                                                checked={(surgical_history.prosthetic_symptoms || []).includes(symptom.value)}
                                                                onCheckedChange={(checked) => {
                                                                    const currentSymptoms = surgical_history.prosthetic_symptoms || [];
                                                                    if (checked) {
                                                                        handleSurgicalChange('prosthetic_symptoms', [...currentSymptoms, symptom.value]);
                                                                    } else {
                                                                        handleSurgicalChange('prosthetic_symptoms', currentSymptoms.filter(s => s !== symptom.value));
                                                                    }
                                                                }}
                                                                data-testid={`prosthetic-symptom-${symptom.value}`}
                                                            />
                                                            <Label htmlFor={`symptom_${symptom.value}`} className="text-slate-700 cursor-pointer">
                                                                {symptom.label}
                                                            </Label>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Dislocation History (for hip PJP) */}
                                        {config.prostheticOptions.dislocationHistory && (
                                            <div className="space-y-3 pt-4 border-t border-slate-300">
                                                <div className="flex items-center space-x-3">
                                                    <Checkbox
                                                        id="has_previous_dislocations"
                                                        checked={surgical_history.has_previous_dislocations || false}
                                                        onCheckedChange={(checked) => handleSurgicalChange('has_previous_dislocations', checked)}
                                                        data-testid="has-previous-dislocations-checkbox"
                                                    />
                                                    <Label htmlFor="has_previous_dislocations" className="font-medium text-slate-700 cursor-pointer">
                                                        Previous Prosthetic Hip Dislocation(s)
                                                    </Label>
                                                </div>

                                                {surgical_history.has_previous_dislocations && (
                                                    <div className="ml-6 space-y-4">
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                            <div className="space-y-2">
                                                                <Label className="label-clinical">Number of Dislocations</Label>
                                                                <Select 
                                                                    value={surgical_history.dislocation_count?.toString() || ''} 
                                                                    onValueChange={(value) => handleSurgicalChange('dislocation_count', parseInt(value))}
                                                                >
                                                                    <SelectTrigger data-testid="dislocation-count-select">
                                                                        <SelectValue placeholder="Select count" />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value="1">1</SelectItem>
                                                                        <SelectItem value="2">2</SelectItem>
                                                                        <SelectItem value="3">3</SelectItem>
                                                                        <SelectItem value="4">4</SelectItem>
                                                                        <SelectItem value="5">5+</SelectItem>
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>

                                                            <div className="space-y-2">
                                                                <Label className="label-clinical">Most Recent Dislocation</Label>
                                                                <Input
                                                                    type="text"
                                                                    placeholder="e.g., January 2024"
                                                                    value={surgical_history.last_dislocation_date || ''}
                                                                    onChange={(e) => handleSurgicalChange('last_dislocation_date', e.target.value)}
                                                                    className="input-clinical"
                                                                    data-testid="last-dislocation-date"
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="space-y-2">
                                                            <Label className="label-clinical">Dislocation Management</Label>
                                                            <Textarea
                                                                placeholder="e.g., Closed reduction under sedation, open reduction required, hip brace prescribed..."
                                                                value={surgical_history.dislocation_management || ''}
                                                                onChange={(e) => handleSurgicalChange('dislocation_management', e.target.value)}
                                                                className="input-clinical"
                                                                data-testid="dislocation-management"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Other Interventions - Condition Specific */}
            <Card className="card-clinical">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {['hand', 'wrist'].includes(body_part) ? (
                            <Hand className="w-5 h-5 text-teal-600" />
                        ) : ['foot', 'ankle'].includes(body_part) ? (
                            <Footprints className="w-5 h-5 text-teal-600" />
                        ) : null}
                        Other Interventions - {body_part.charAt(0).toUpperCase() + body_part.slice(1)}
                    </CardTitle>
                    <CardDescription>
                        Select interventions relevant to your patient's {body_part} condition
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {config.otherInterventions.map((intervention) => (
                            <div key={intervention.id} className="space-y-2">
                                <div className="flex items-center space-x-3">
                                    <Checkbox
                                        id={intervention.id}
                                        checked={treatment_history[intervention.id] || false}
                                        onCheckedChange={(checked) => handleTreatmentChange(intervention.id, checked)}
                                        data-testid={`${intervention.id}-checkbox`}
                                    />
                                    <Label htmlFor={intervention.id} className="font-medium text-slate-700 cursor-pointer">
                                        {intervention.label}
                                    </Label>
                                </div>
                                {intervention.hasDetail && treatment_history[intervention.id] && (
                                    <Input
                                        placeholder={intervention.detailPlaceholder}
                                        value={treatment_history[`${intervention.id}_type`] || ''}
                                        onChange={(e) => handleTreatmentChange(`${intervention.id}_type`, e.target.value)}
                                        className="ml-6 input-clinical"
                                        data-testid={`${intervention.id}-type`}
                                    />
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="space-y-2 pt-4">
                        <Label className="label-clinical">Additional Lifestyle Modifications</Label>
                        <Textarea
                            placeholder="e.g., Activity modification, ergonomic changes, dietary changes, alternative therapies tried"
                            value={treatment_history.lifestyle_modifications || ''}
                            onChange={(e) => handleTreatmentChange('lifestyle_modifications', e.target.value)}
                            className="input-clinical"
                            data-testid="lifestyle-mods-textarea"
                        />
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
